import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Chip,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Straighten as StraightenIcon,
  Badge as BadgeIcon,
  SwapHoriz as SwapHorizIcon,
  ViewColumn as ViewColumnIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { useStore } from 'react-admin';
import type { SizeProperty, SizePropertyInput, Partner } from '@src/types';
import * as RewareApi from '@src/apis/reware';

export const EditSizeCodeMapping: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sizeProperty, setSizeProperty] = useState<SizeProperty | null>(null);
  const [formData, setFormData] = useState<SizePropertyInput>({
    code: '',
    display: '',
    synonyms: [],
    family: [],
    primary_value: '',
    primary_label: '',
    secondary_value: '',
    secondary_label: '',
    tertiary_value: '',
    tertiary_label: '',
  });
  const [newSynonym, setNewSynonym] = useState('');
  const [newFamily, setNewFamily] = useState('');

  useEffect(() => {
    loadSizeProperty();
  }, [id, currentPartner]);

  const extractErrorMessage = (error: any, fallback: string): string => {
    if (error?.response?.data?.errors?.[0]?.message) {
      return error.response.data.errors[0].message;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return fallback;
  };

  const loadSizeProperty = async () => {
    if (!currentPartner || !id) {
      console.warn('No partner selected or invalid ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      // Fetch all properties across all pages to find the one with matching ID
      const allProperties: SizeProperty[] = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const response = await RewareApi.getSizeProperties({
          partnerCode: currentPartner.code,
          filter: {},
          page: currentPage,
          perPage: 200,
        });

        allProperties.push(...response.data);
        totalPages = Math.ceil(response.total / 200);
        currentPage++;
      } while (currentPage <= totalPages);

      const property = allProperties.find((p) => p.id === parseInt(id));
      if (property) {
        setSizeProperty(property);
        setFormData({
          code: property.code,
          display: property.display,
          synonyms: property.synonyms || [],
          family: property.family || [],
          primary_value: property.primary_value || '',
          primary_label: property.primary_label || '',
          secondary_value: property.secondary_value || '',
          secondary_label: property.secondary_label || '',
          tertiary_value: property.tertiary_value || '',
          tertiary_label: property.tertiary_label || '',
        });
      } else {
        setSizeProperty(null);
      }
    } catch (error) {
      console.error('Failed to load size property:', error);
      setSizeProperty(null);
      setLoadError(extractErrorMessage(error, 'Failed to load this size mapping. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/catalog/attributes?tab=sizes');
  };

  const handleSave = async () => {
    if (!currentPartner || !id) return;

    setSaving(true);
    setSaveError('');
    try {
      await RewareApi.updateSizeProperty({
        id: parseInt(id),
        partnerCode: currentPartner.code,
        token: '', // execAdmin will handle token
        data: formData,
      });

      // Navigate back to list on success
      navigate('/catalog/attributes?tab=sizes');
    } catch (error) {
      console.error('Failed to save size property:', error);
      setSaveError(extractErrorMessage(error, 'Failed to save size mapping. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentPartner || !id) return;

    setDeleting(true);
    setDeleteError('');
    try {
      await RewareApi.deleteSizeProperty({
        id: parseInt(id),
        partnerCode: currentPartner.code,
        token: '', // execAdmin will handle token
      });

      // Navigate back to list on success
      navigate('/catalog/attributes?tab=sizes');
    } catch (error) {
      console.error('Failed to delete size property:', error);
      setDeleteError(extractErrorMessage(error, 'Failed to delete size mapping. Please try again.'));
      setDeleting(false);
    }
  };

  const updateField = (field: keyof SizePropertyInput, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addSynonym = () => {
    if (newSynonym.trim()) {
      updateField('synonyms', [...(formData.synonyms || []), newSynonym.trim()]);
      setNewSynonym('');
    }
  };

  const removeSynonym = (index: number) => {
    const newSynonyms = [...(formData.synonyms || [])];
    newSynonyms.splice(index, 1);
    updateField('synonyms', newSynonyms);
  };

  const addFamily = () => {
    if (newFamily.trim()) {
      updateField('family', [...(formData.family || []), newFamily.trim()]);
      setNewFamily('');
    }
  };

  const removeFamily = (index: number) => {
    const newFamilies = [...(formData.family || [])];
    newFamilies.splice(index, 1);
    updateField('family', newFamilies);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sizeProperty) {
    return (
      <Box sx={{ p: 3 }}>
        {loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <Typography variant="h5" color="error">
            Size property not found
          </Typography>
        )}
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Size Mappings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StraightenIcon /> Edit Size Code Mapping
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Size Mappings
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Map a catalog code to its display name and size grouping
      </Typography>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {saveError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError('')}>
              {saveError}
            </Alert>
          )}

          {/* Size Identity */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon /> Size Identity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The catalog code and the customer-facing name it maps to.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Size Code"
                required
                fullWidth
                value={formData.code}
                onChange={(e) => updateField('code', e.target.value)}
                helperText='The code used in your catalog data (e.g., "M", "32x34")'
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Size Display Name"
                required
                fullWidth
                value={formData.display}
                onChange={(e) => updateField('display', e.target.value)}
                helperText='Customer-facing size name (e.g., "Medium", "32 x 34")'
              />
            </Grid>
          </Grid>

          {/* Size Groups */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StraightenIcon /> Size Groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Groups drive storefront size filtering. Add as many as apply.
          </Typography>

          <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.family?.map((group, index) => (
                <Chip key={index} label={group} onDelete={() => removeFamily(index)} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Add size group"
                value={newFamily}
                onChange={(e) => setNewFamily(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addFamily()}
                fullWidth
              />
              <Button onClick={addFamily} variant="outlined">
                Add
              </Button>
            </Box>
          </Card>

          {/* Size Synonyms */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHorizIcon /> Size Synonyms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Other size codes that should map to this size.
          </Typography>

          <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.synonyms?.map((synonym, index) => (
                <Chip key={index} label={synonym} onDelete={() => removeSynonym(index)} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Add synonym"
                value={newSynonym}
                onChange={(e) => setNewSynonym(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addSynonym()}
                fullWidth
              />
              <Button onClick={addSynonym} variant="outlined">
                Add
              </Button>
            </Box>
          </Card>

          {/* Size Components */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewColumnIcon /> Size Components
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If size is made of multiple measurements, define each component so shoppers can select them separately on the product page.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                  PRIMARY COMPONENT
                  <Chip label="Optional" size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Display Value"
                      fullWidth
                      size="small"
                      value={formData.primary_value}
                      onChange={(e) => updateField('primary_value', e.target.value)}
                      placeholder='e.g., "Tall"'
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Label"
                      fullWidth
                      size="small"
                      value={formData.primary_label}
                      onChange={(e) => updateField('primary_label', e.target.value)}
                      placeholder='e.g., "Size Range"'
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                  SECONDARY COMPONENT
                  <Chip label="Optional" size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Display Value"
                      fullWidth
                      size="small"
                      value={formData.secondary_value}
                      onChange={(e) => updateField('secondary_value', e.target.value)}
                      placeholder='e.g., "32"'
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Label"
                      fullWidth
                      size="small"
                      value={formData.secondary_label}
                      onChange={(e) => updateField('secondary_label', e.target.value)}
                      placeholder='e.g., "Waist"'
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                  TERTIARY COMPONENT
                  <Chip label="Optional" size="small" sx={{ ml: 1, fontSize: '0.7rem' }} />
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Display Value"
                      fullWidth
                      size="small"
                      value={formData.tertiary_value}
                      onChange={(e) => updateField('tertiary_value', e.target.value)}
                      placeholder='e.g., "30"'
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Label"
                      fullWidth
                      size="small"
                      value={formData.tertiary_label}
                      onChange={(e) => updateField('tertiary_label', e.target.value)}
                      placeholder='e.g., "Length"'
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>

          {/* Product Page Preview */}
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon /> Product Page Preview
          </Typography>
          <Card variant="outlined" sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
            <Box sx={{ display: 'flex', gap: 4 }}>
              {formData.primary_value && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formData.primary_label || '—'}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formData.primary_value}
                  </Typography>
                </Box>
              )}
              {formData.secondary_value && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formData.secondary_label || '—'}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formData.secondary_value}
                  </Typography>
                </Box>
              )}
              {formData.tertiary_value && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formData.tertiary_label || '—'}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formData.tertiary_value}
                  </Typography>
                </Box>
              )}
              {!formData.primary_value && !formData.secondary_value && !formData.tertiary_value && (
                <Typography variant="body2" color="text.secondary">
                  Add size components above to see the product page preview.
                </Typography>
              )}
            </Box>
          </Card>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={handleBack}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving || !formData.code || !formData.display}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => (!deleting ? setDeleteDialogOpen(false) : undefined)}>
        <DialogTitle>Delete Size Code Mapping?</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete the size code mapping for &quot;{formData.code}&quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
